import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import Notification from "@/models/notifications/Notification";
import UnreadNotifications from "@/models/notifications/UnreadNotifications";
import {
  NotificationCategory,
  toNotificatonCategory,
  toString,
} from "@/models/notifications/NotificationCategory";
import { AxiosError } from "axios";
import store from "../store";
import cloneDeep  from "lodash/cloneDeep";
import Vue from "vue";
import {Emit} from "@/api/apiHelper";

/**
 * 🧩 Module which is responsible for managing notifications
 */
@Module
export default class NotificationsModule extends VuexModule {

  // 📦 Notifications which are fetched from the API
  public notifications: Notification[] = [];

  // 📦 Unread notification numbers grouped by category
  public unreadNotifications: UnreadNotifications | null = null;

  // 📦 Notification category by which the paginated results rely on
  public notificationCategoryToFilterBy?: NotificationCategory | null = null;

  // 📦 Page number in the pagination of the results
  public page: number = 1;

  // 📦 Total number of pages of the pagination
  public totalPages: number = 1;

  /**
   * 👾 Mutation which sets the unread notifications number object
   * @param unreadNotifications
   */
  @Mutation
  public setUnreadNotificationsNumber(
    unreadNotifications: UnreadNotifications
  ) {
    this.unreadNotifications = unreadNotifications;
  }

  /**
   * 👾 Mutation which sets the notification for the current user
   * @param newNotifications
   */
  @Mutation
  public setNotifications(newNotifications: Notification[]) {
    this.notifications = newNotifications;
  }

  /**
   * 👾 Mutation which sets the notification category the pagination uses to filter the resultsF
   * @param category
   */
  @Mutation
  public setNotificationCategoryToFilter(
    category: NotificationCategory | null
  ) {
    this.notificationCategoryToFilterBy = category;
    store.commit("setNotificationsPageNumber", 1);
  }

  /**
   * 👾 Mutation which sets the page for the resulting page of the pagination
   * @param pageNumber
   */
  @Mutation
  public setNotificationsPageNumber(pageNumber: number) {
    this.page = pageNumber;
  }

  /**
   * 👾 Mutation which sets the total page for the pagination
   * @param totalPages
   */
  @Mutation
  public setNotificationsTotalPageNumber(totalPages: number) {
    this.totalPages = totalPages;
  }

  /**
   * 🚀 Fetches the current user's notifications
   */
  @Action
  public fetchNotifications() {
    Vue.prototype.$api
      .get("/api/v1/notifications", {
        page: this.page,
        category: toString(this.notificationCategoryToFilterBy!!),
      }, Emit.None)
      .then((response: any) => {
        let notifications: Notification[] = [];
        let notificationPairs = Object.entries(response.data.result);

        // Transform the raw data to Notification object
        notificationPairs.forEach((notificationPair: Object) => {
          let id = Object.values(notificationPair)[0];
          let value = Object.values(notificationPair)[1];

          let newNotification = new Notification(
            id,
            value.title,
            value.text,
            toNotificatonCategory(value.category),
            value.type,
            value.icon,
            value.link,
            value.seen,
            value.timestamp
          );
          notifications.push(newNotification);
        });
        notifications = notifications.sort((a: Notification, b: Notification) => b.timestamp - a.timestamp);

        store.commit("setNotifications", notifications);
        store.commit("setUnreadNotificationsNumber", response.data.unseen);
        store.commit("setNotificationsTotalPageNumber", response.data.pages);
      })
      .catch((error: AxiosError) => {
        store.commit("setNotifications", []);
      });
  }

  /**
   * 🚀 Action which marks a certain notification as read and sends it to the API
   */
  @Action
  public markNotificationAsSeen(notificationId: string) {
    Vue.prototype.$api
      .post("/api/v1/notifications", {
        notification_id: notificationId,
      })
      .then((response: any) => {
        // Adjust seen property in the local copy of the notifications
        let notificationToBeSeen = this.notifications.find(
          (notification: Notification) => {
            return notification.id == notificationId;
          }
        )!!;

        notificationToBeSeen.seen = true;

        let modifiedUnreadMessages: UnreadNotifications = cloneDeep(
          this.unreadNotifications!!
        );
        switch (notificationToBeSeen.category) {
          case NotificationCategory.Trade:
            modifiedUnreadMessages.trade--;
          case NotificationCategory.System:
            modifiedUnreadMessages.system--;
          case NotificationCategory.News:
            modifiedUnreadMessages.news--;
          case NotificationCategory.Promotions:
            modifiedUnreadMessages.promo--;
        }
        modifiedUnreadMessages.all--;
        store.commit("setUnreadNotificationsNumber", modifiedUnreadMessages);
      });
  }

  /**
   * 🚀 Action which marks all notifications of the currently selected category as read
   */
  @Action
  public readAllNotificationsByCategory() {
    Vue.prototype.$api
      .post("/api/v1/readallnotifications", {
        category: toString(this.notificationCategoryToFilterBy!!),
      })
      .then((response: any) => {
        store.dispatch("fetchNotifications");
      });
  }
}
